import React from 'react';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Checkbox } from '../../components/ui/checkbox';
import { Eye, Lock, Paperclip } from 'lucide-react';
import { TYPE_LABELS, TYPE_COLORS } from './constants';

export function PreviewTab({ sections, form }) {
  if (!sections || sections.length === 0) {
    return (
      <div className="text-center py-12 text-slate-400">
        <Eye className="w-10 h-10 mx-auto mb-2 opacity-40" />
        <p className="text-sm">No questions to preview. Add sections and questions first.</p>
      </div>
    );
  }

  return (
    <div className="py-2" data-testid="preview-tab">
      <div className="bg-slate-50 border rounded-lg p-6 max-w-3xl mx-auto">
        <div className="border-b pb-4 mb-6">
          <Badge className={`${TYPE_COLORS[form.type] || ''} text-xs mb-2`}>{TYPE_LABELS[form.type]}</Badge>
          <h2 className="text-xl font-bold text-slate-800">{form.title || 'Untitled Event'}</h2>
          {form.description && <p className="text-sm text-slate-600 mt-2">{form.description}</p>}
          {form.closing_date && (
            <p className="text-xs text-red-600 mt-2 font-medium">Deadline: {form.closing_date.replace('T', ' at ')}</p>
          )}
          {form.sealed_bidding?.enabled && (
            <div className="flex items-center gap-1.5 mt-2 text-amber-700 text-xs"><Lock className="w-3.5 h-3.5" /> Sealed Bidding — responses hidden until reveal date</div>
          )}
        </div>

        {sections.map((section, sIdx) => (
          <div key={sIdx} className="mb-6">
            <h3 className="text-base font-semibold text-slate-700 border-b pb-1 mb-3">{section.section_name}</h3>
            <div className="space-y-4">
              {section.questions.map((q, qIdx) => (
                <div key={q.question_id || qIdx} className="bg-white rounded border p-4">
                  <div className="flex items-start gap-2">
                    <span className="text-xs text-slate-400 font-mono mt-0.5">{sIdx + 1}.{qIdx + 1}</span>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-slate-800">
                        {q.text || 'Untitled Question'}
                        {q.required && <span className="text-red-500 ml-1">*</span>}
                      </p>
                      <div className="mt-2">
                        {q.type === 'free_text' && <Textarea disabled placeholder="Supplier enters their response here..." className="text-sm bg-slate-50 h-20" />}
                        {q.type === 'yes_no' && (
                          <div className="flex gap-4">
                            <label className="flex items-center gap-2 text-sm"><input type="radio" disabled name={`prev_${q.question_id}`} /> Yes</label>
                            <label className="flex items-center gap-2 text-sm"><input type="radio" disabled name={`prev_${q.question_id}`} /> No</label>
                          </div>
                        )}
                        {q.type === 'multiple_choice' && (
                          <div className="space-y-1.5">
                            {(q.options || []).map((opt, oIdx) => (
                              <label key={oIdx} className="flex items-center gap-2 text-sm"><Checkbox disabled /> {opt}</label>
                            ))}
                            {(!q.options || q.options.length === 0) && <p className="text-xs text-slate-400 italic">No options defined</p>}
                          </div>
                        )}
                        {q.type === 'numeric' && <Input type="number" disabled placeholder="Enter a number" className="max-w-[200px] text-sm bg-slate-50" />}
                        {q.type === 'date' && <Input type="date" disabled className="max-w-[200px] text-sm bg-slate-50" />}
                        {q.type === 'rating' && (
                          <div className="flex gap-1">
                            {[1,2,3,4,5,6,7,8,9,10].map(n => (
                              <button key={n} disabled className="w-7 h-7 rounded border text-xs text-slate-400 bg-slate-50">{n}</button>
                            ))}
                          </div>
                        )}
                        {q.type === 'file_upload' && (
                          <div className="border-2 border-dashed rounded p-3 text-center text-xs text-slate-400">
                            <Paperclip className="w-4 h-4 mx-auto mb-1" /> Click or drag to upload file
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}

        <div className="border-t pt-4 mt-6 text-center">
          <Button disabled className="bg-indigo-600 text-white opacity-60">Submit Response</Button>
          <p className="text-xs text-slate-400 mt-2">This is a preview. Suppliers will see this form when responding.</p>
        </div>
      </div>
    </div>
  );
}
